import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import SettingServices from "../../../services/SettingServices";

/**
 * Deposit discount and withdrawal fee. Both have their own endpoints, so this
 * tab loads and saves independently of the main settings row.
 */
export default function WalletSettings() {
  const settingApi = new SettingServices();

  const [discountEnabled, setDiscountEnabled] = useState(false);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [discountMinAmount, setDiscountMinAmount] = useState(500);
  const [discountLoading, setDiscountLoading] = useState(false);

  const [feeEnabled, setFeeEnabled] = useState(false);
  const [feePercentage, setFeePercentage] = useState(3);
  const [feeMinAmount, setFeeMinAmount] = useState(100);
  const [feeLoading, setFeeLoading] = useState(false);

  useEffect(() => {
    settingApi
      .getDepositDiscount()
      .then((res) => {
        if (res?.isSuccess) {
          setDiscountEnabled(Boolean(res.data.enabled));
          setDiscountPercentage(Number(res.data.percentage || 0));
          setDiscountMinAmount(Number(res.data.min_amount || 0));
        }
      })
      .catch(() => {
        /* non-fatal: the section just shows defaults */
      });

    settingApi
      .getWithdrawalFee()
      .then((res) => {
        if (res?.isSuccess) {
          setFeeEnabled(Boolean(res.data.enabled));
          setFeePercentage(Math.round(Number(res.data.percentage || 0)));
          setFeeMinAmount(Number(res.data.min_amount || 0));
        }
      })
      .catch(() => {
        /* non-fatal */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveDiscount = async () => {
    const pct = Number(discountPercentage);
    const min = Number(discountMinAmount);

    // Whole percent only - matches the INT column and the server rule.
    if (!Number.isInteger(pct) || pct < 0 || pct > 100) {
      toast.error("Discount percentage must be a whole number between 1 and 100");
      return;
    }
    if (!Number.isFinite(min) || min < 0) {
      toast.error("Minimum deposit amount must be zero or more");
      return;
    }
    if (discountEnabled && pct <= 0) {
      toast.error("Set a discount percentage above 0 before enabling it");
      return;
    }

    setDiscountLoading(true);
    try {
      const res = await settingApi.updateDepositDiscount({
        enabled: discountEnabled ? 1 : 0,
        percentage: pct,
        min_amount: min,
      });
      if (res.isSuccess) {
        toast.success(res.message);
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to update deposit discount",
      );
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleSaveWithdrawalFee = async () => {
    const pct = Number(feePercentage);
    const min = Number(feeMinAmount);

    // Whole percent only - matches the INT column and the server rule.
    if (!Number.isInteger(pct) || pct < 1 || pct > 100) {
      toast.error("Withdrawal fee must be a whole number between 1 and 100");
      return;
    }
    if (!Number.isFinite(min) || min < 0) {
      toast.error("Minimum withdrawal amount must be zero or more");
      return;
    }
    if (feeEnabled && pct <= 0) {
      toast.error("Set a fee percentage above 0 before enabling it");
      return;
    }

    setFeeLoading(true);
    try {
      const res = await settingApi.updateWithdrawalFee({
        enabled: feeEnabled ? 1 : 0,
        percentage: pct,
        min_amount: min,
      });
      if (res.isSuccess) {
        toast.success(res.message);
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to update withdrawal fee",
      );
    } finally {
      setFeeLoading(false);
    }
  };

  return (
    <>
      <div className="discount-control">
        <h5 className="settings-section-title">Deposit Discount</h5>
        <p className="discount-help">
          Percentage bonus credited to mobile users on approved deposits. The
          bonus is debited from the approving parent's wallet along with the
          deposit amount.
        </p>

        <div className="game-row">
          <div>
            <strong>Discount Status</strong>
            <small className={discountEnabled ? "live" : "stopped"}>
              {discountEnabled ? "ACTIVE" : "INACTIVE"}
            </small>
          </div>
          <button
            className={discountEnabled ? "btn-stop" : "btn-start"}
            onClick={() => setDiscountEnabled((v) => !v)}
          >
            {discountEnabled ? "DEACTIVATE" : "ACTIVATE"}
          </button>
        </div>

        <div className="input-grid">
          <div className="input-box">
            <label>Discount Percentage (%)</label>
            <input
              type="number"
              min="1"
              max="100"
              step="1"
              value={discountPercentage}
              onChange={(e) => {
                // Strip anything that is not a digit so a pasted "10.01"
                // or a typed decimal point cannot reach state.
                const digits = e.target.value.replace(/\D/g, "");
                if (digits === "") {
                  setDiscountPercentage("");
                  return;
                }
                setDiscountPercentage(Math.min(100, Number(digits)));
              }}
              onKeyDown={(e) => {
                if ([".", ",", "e", "E", "+", "-"].includes(e.key)) {
                  e.preventDefault();
                }
              }}
            />
          </div>
          <div className="input-box">
            <label>Minimum Deposit Amount (₹)</label>
            <input
              type="number"
              min="0"
              value={discountMinAmount}
              onChange={(e) => setDiscountMinAmount(e.target.value)}
            />
          </div>
        </div>

        {discountEnabled && Number(discountPercentage) > 0 && (
          <p className="discount-preview">
            A ₹{Number(discountMinAmount) || 0} deposit will credit ₹
            {(
              (Number(discountMinAmount) || 0) *
              (1 + Number(discountPercentage) / 100)
            ).toFixed(2)}
            . Deposits below ₹{Number(discountMinAmount) || 0} get no bonus.
          </p>
        )}

        <button
          className="save-btn"
          onClick={handleSaveDiscount}
          disabled={discountLoading}
        >
          {discountLoading ? "Saving..." : "Save Discount Settings"}
        </button>
      </div>

      <hr />

      <div className="discount-control">
        <h5 className="settings-section-title">Withdrawal Fee</h5>
        <p className="discount-help">
          Percentage held back from a player's withdrawal. Only the remainder
          leaves their wallet and it is the exact figure the approving parent is
          credited and must transfer to the bank. The fee itself is credited to
          no one — it stays in the player's wallet as locked credit they can bet
          with but never withdraw, which is what stops the fee being avoided by
          splitting one withdrawal into several.
        </p>

        <div className="game-row">
          <div>
            <strong>Fee Status</strong>
            <small className={feeEnabled ? "live" : "stopped"}>
              {feeEnabled ? "ACTIVE" : "INACTIVE"}
            </small>
          </div>
          <button
            className={feeEnabled ? "btn-stop" : "btn-start"}
            onClick={() => setFeeEnabled((v) => !v)}
          >
            {feeEnabled ? "DEACTIVATE" : "ACTIVATE"}
          </button>
        </div>

        <div className="input-grid">
          <div className="input-box">
            <label>Withdrawal Fee (%)</label>
            <input
              type="number"
              min="1"
              max="100"
              step="1"
              value={feePercentage}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "");
                if (digits === "") {
                  setFeePercentage("");
                  return;
                }
                setFeePercentage(Math.min(100, Number(digits)));
              }}
              onKeyDown={(e) => {
                if ([".", ",", "e", "E", "+", "-"].includes(e.key)) {
                  e.preventDefault();
                }
              }}
            />
          </div>
          <div className="input-box">
            <label>Minimum Withdrawal Amount (₹)</label>
            <input
              type="number"
              min="0"
              value={feeMinAmount}
              onChange={(e) => setFeeMinAmount(e.target.value)}
            />
          </div>
        </div>

        {feeEnabled && Number(feePercentage) > 0 && (
          <p className="fee-preview">
            A ₹500 withdrawal pays the player ₹
            {(500 - (500 * Number(feePercentage)) / 100).toFixed(2)} and debits
            their wallet by the same amount. The ₹
            {((500 * Number(feePercentage)) / 100).toFixed(2)} fee stays in their
            wallet, locked. Deposit bonuses and locked fees can never be
            withdrawn.
          </p>
        )}

        <button
          className="save-btn"
          onClick={handleSaveWithdrawalFee}
          disabled={feeLoading}
        >
          {feeLoading ? "Saving..." : "Save Withdrawal Fee"}
        </button>
      </div>
    </>
  );
}
